import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Chip, 
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormGroup,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import { useEvents } from '../contexts/EventsContext';
import { useUser } from '../contexts/UserContext';
import { useSchools } from '../contexts/SchoolsContext';
import { activityLogService } from '../services/activityLogService';
import { ref, update, remove, get, set } from 'firebase/database';
import { database } from '../firebase';
import MapModal from '../components/MapModal';

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { events, loading, error, refresh } = useEvents();
  const { user } = useUser();
  const { schools } = useSchools();
  
  // State for modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  
  // State for calendar functionality
  const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<null | HTMLElement>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarEventData, setCalendarEventData] = useState<any>(null);
  
  // State to force re-render after edit
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Local event state that can be updated immediately
  const [localEvent, setLocalEvent] = useState<any>(null);
  
  // State for edit form
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    location: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    website: '',
    registerRequired: false,
    eventType: 'physical',
    hostName: '',
    private: false
  });

  // State for parsed start/end date/time for display
  const [parsedStartDate, setParsedStartDate] = useState('');
  const [parsedStartTime, setParsedStartTime] = useState('');
  const [parsedEndDate, setParsedEndDate] = useState('');
  const [parsedEndTime, setParsedEndTime] = useState('');

  // Handler for edit form changes with auto-population logic
  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditForm(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };
      
      // Auto-set End Date to Start Date when Start Date is selected (only if End Date is empty)
      if (field === 'startDate' && typeof value === 'string' && value && !prev.endDate) {
        updatedData.endDate = value;
      }
      
      // Auto-set End Time to Start Time + 1 hour when Start Time is selected (only if End Time is empty)
      if (field === 'startTime' && typeof value === 'string' && value && !prev.endTime) {
        const startTime = new Date(`2000-01-01T${value}`);
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
        updatedData.endTime = endTime.toTimeString().slice(0, 5); // Format as HH:MM
      }
      
      // Auto-populate Physical Location with "Virtual" when Event Type is set to Virtual Event
      if (field === 'eventType' && value === 'virtual') {
        updatedData.location = 'Virtual';
      }
      
      return updatedData;
    });
  };

  // Try to get event from context by id
  const event = React.useMemo(() => {
    if (id) {
      return events.find((e: any) => e.id === id);
    }
    return undefined;
  }, [id, events, refreshTrigger]);
  
  // Use local event if available, otherwise use the event from context
  const displayEvent = localEvent || event;

  // Log event pageview when event is loaded
  React.useEffect(() => {
    if (displayEvent && id && user?.uid) {
      try {
        activityLogService.logEventPageview(user.uid, id);
      } catch (error) {
        console.error('Error logging event pageview activity:', error);
        // Don't block the flow if logging fails
      }
    }
  }, [displayEvent, id, user?.uid]);

  // Find the school associated with this event
  const eventSchool = React.useMemo(() => {
    if (!displayEvent) return null;
    const hostName = displayEvent.hostName || displayEvent.host;
    return schools.find(school => school.name === hostName);
  }, [displayEvent, schools]);

  // Get the appropriate school logo using the same logic as EventsList
  const getSchoolLogo = (): string | undefined => {
    if (!displayEvent) return undefined;
    const hostName = displayEvent.hostName || displayEvent.host;
    
    // Priority 1: User's school icon if event host matches user's school
    if (hostName === user?.schoolName && user?.userIcon) {
      return user.userIcon;
    }
    
    // Priority 2: School logo from schools data
    if (eventSchool && (eventSchool.feedIcon || eventSchool.resultsImage)) {
      return eventSchool.feedIcon || eventSchool.resultsImage;
    }
    
    // Priority 3: Fosterway fallback
    if (hostName.toLowerCase().includes('fosterway')) {
      return "https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd";
    }
    
    return undefined;
  };

  // Calendar helper functions
  const formatDateForICS = (dateTimeStr: string): string => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr.replace(' ', 'T'));
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const formatDateForGoogleCalendar = (dateTimeStr: string): string => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr.replace(' ', 'T'));
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const formatDateForOutlook = (dateTimeStr: string): string => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr.replace(' ', 'T'));
    return date.toISOString().split('.')[0]; // YYYY-MM-DDTHH:MM:SS format
  };

  const generateICS = (event: any): string => {
    // Debug: Log event data to see what location fields are available
    console.log('Event data for ICS generation:', {
      id: event.id,
      title: event.title || event.name,
      address: event.address,
      location: event.location,
      physicalLocation: event.physicalLocation,
      description: event.description,
      hostName: event.hostName,
      host: event.host,
      website: event.website
    });
    
    // Get location information - try multiple possible fields
    let location = event.address || event.location || event.physicalLocation || '';
    
    // Handle virtual events - include website in location if available
    if (location.toLowerCase() === 'virtual' && event.website) {
      location = `Virtual Event - ${event.website}`;
    } else if (location.toLowerCase() === 'virtual') {
      location = 'Virtual Event';
    }
    
    // Get school logo URL for the description
    const getSchoolLogoForDescription = (): string => {
      const hostName = event.hostName || event.host;
      
      // Priority 1: User's school icon if event host matches user's school
      if (hostName === user?.schoolName && user?.userIcon) {
        return user.userIcon;
      }
      
      // Priority 2: School logo from schools data
      const school = schools.find(s => s.name === hostName);
      if (school && (school.feedIcon || school.resultsImage)) {
        return school.feedIcon || school.resultsImage || '';
      }
      
      // Priority 3: Fosterway fallback
      if (hostName.toLowerCase().includes('fosterway')) {
        return "https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd";
      }
      
      return '';
    };

    // Get description with enhanced formatting
    const schoolLogoUrl = getSchoolLogoForDescription();
    const hostName = event.hostName || event.host;
    
    const description = [
      // First line: School image and host name
      schoolLogoUrl && hostName ? `🏫 ${hostName}` : hostName ? `🏫 ${hostName}` : '',
      // Blank line
      '',
      // Event description
      event.description || '',
      // Additional information
      event.website ? `Website: ${event.website}` : '',
      (location || '').toLowerCase() === 'virtual' ? 'This is a virtual event. Please check the website for joining instructions.' : ''
    ].filter(Boolean).join('\n');
    
    // Helper function to escape ICS field values
    const escapeICSValue = (value: string | undefined): string => {
      if (!value) return '';
      return value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r');
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Fosterway//Event Calendar//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@fosterway.com`,
      `DTSTART:${formatDateForICS(event.startTime)}`,
      event.endTime ? `DTEND:${formatDateForICS(event.endTime)}` : '',
      `SUMMARY:${escapeICSValue(event.title || event.name)}`,
      `DESCRIPTION:${escapeICSValue(description)}`,
      location ? `LOCATION:${escapeICSValue(location)}` : '',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line).join('\r\n');
    
    console.log('Generated ICS content:', icsContent);
    
    return icsContent;
  };

  const downloadICS = (event: any) => {
    const icsContent = generateICS(event);
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title || event.name}.ics`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getCalendarUrls = (event: any) => {
    const title = encodeURIComponent(event.title || event.name);
    const description = encodeURIComponent(event.description || '');
    const location = encodeURIComponent(event.address || event.location || '');
    
    // Format dates properly for each calendar service
    const googleStartTime = event.startTime ? formatDateForGoogleCalendar(event.startTime) : '';
    const googleEndTime = event.endTime ? formatDateForGoogleCalendar(event.endTime) : '';
    const outlookStartTime = event.startTime ? formatDateForOutlook(event.startTime) : '';
    const outlookEndTime = event.endTime ? formatDateForOutlook(event.endTime) : '';
    
    return {
      google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${description}&location=${location}&dates=${googleStartTime}/${googleEndTime}`,
      outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&body=${description}&location=${location}&startdt=${outlookStartTime}&enddt=${outlookEndTime}`
    };
  };

  // Check if event is already added to calendar
  const isEventInCalendar = (): boolean => {
    if (!displayEvent) return false;
    
    // Check local state first
    if (calendarEventData) {
      return !calendarEventData.timestampCancelled;
    }
    
    // Check user context
    if (!user?.profileExtra?.eventsAddedToCalendar) return false;
    const eventData = user.profileExtra.eventsAddedToCalendar[displayEvent.id];
    return eventData && !eventData.timestampCancelled;
  };

  // Get calendar event data
  const getCalendarEventData = () => {
    if (!displayEvent) return null;
    
    // Return local state if available
    if (calendarEventData) {
      return calendarEventData;
    }
    
    // Return from user context
    if (!user?.profileExtra?.eventsAddedToCalendar) return null;
    return user.profileExtra.eventsAddedToCalendar[displayEvent.id];
  };

  // Format date for display
  const formatCalendarDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Track calendar event addition
  const trackCalendarEvent = async (calendarType: string) => {
    if (!user?.uid || !displayEvent) return;
    
    setCalendarLoading(true);
    try {
      const userRef = ref(database, `user/${user.uid}`);
      const snapshot = await get(userRef);
      const existingUserData = snapshot.exists() ? snapshot.val() : {};
      
      const existingProfileExtra = existingUserData.profileExtra || {};
      const existingEventsAddedToCalendar = existingProfileExtra.eventsAddedToCalendar || {};
      
      const calendarEventData = {
        eventId: displayEvent.id,
        calendarType,
        timestampAdded: Date.now(),
        userDeviceInfo: navigator.userAgent,
        timestampCancelled: null
      };
      
      const updatedProfileExtra = {
        ...existingProfileExtra,
        eventsAddedToCalendar: {
          ...existingEventsAddedToCalendar,
          [displayEvent.id]: calendarEventData
        }
      };
      
      await update(ref(database), {
        [`user/${user.uid}/profileExtra`]: updatedProfileExtra
      });
      
      // Update local state to reflect the change immediately
      setCalendarEventData(calendarEventData);
      
      // Log the event added to calendar activity
      try {
        await activityLogService.logEventAddedToCalendar(user.uid, displayEvent.id);
      } catch (error) {
        console.error('Error logging event added to calendar activity:', error);
        // Don't block the flow if logging fails
      }
      
    } catch (error) {
      console.error('Error tracking calendar event:', error);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Handle calendar action
  const handleCalendarAction = async (calendarType: string) => {
    if (!displayEvent) return;
    
    // Track the calendar event
    await trackCalendarEvent(calendarType);
    
    // Perform the calendar action
    if (calendarType === 'ics') {
      downloadICS(displayEvent);
    } else {
      const urls = getCalendarUrls(displayEvent);
      const url = urls[calendarType as keyof typeof urls];
      if (url) {
        window.open(url, '_blank');
      }
    }
    
    setCalendarMenuOpen(false);
  };

  // Handle removing event from calendar tracking
  const handleRemoveFromCalendar = async () => {
    if (!user?.uid || !displayEvent) return;
    
    setCalendarLoading(true);
    try {
      const userRef = ref(database, `user/${user.uid}`);
      const snapshot = await get(userRef);
      const existingUserData = snapshot.exists() ? snapshot.val() : {};
      
      const existingProfileExtra = existingUserData.profileExtra || {};
      const existingEventsAddedToCalendar = existingProfileExtra.eventsAddedToCalendar || {};
      
      // Update the existing calendar event data with cancellation timestamp
      const updatedCalendarEventData = {
        ...existingEventsAddedToCalendar[displayEvent.id],
        timestampCancelled: Date.now()
      };
      
      const updatedProfileExtra = {
        ...existingProfileExtra,
        eventsAddedToCalendar: {
          ...existingEventsAddedToCalendar,
          [displayEvent.id]: updatedCalendarEventData
        }
      };
      
      await update(ref(database), {
        [`user/${user.uid}/profileExtra`]: updatedProfileExtra
      });
      
      // Update local state to reflect the change immediately
      setCalendarEventData(updatedCalendarEventData);
      
      // Log the event removed from calendar activity
      try {
        await activityLogService.logEventRemovedFromCalendar(user.uid, displayEvent.id);
      } catch (error) {
        console.error('Error logging event removed from calendar activity:', error);
        // Don't block the flow if logging fails
      }
      
    } catch (error) {
      console.error('Error removing calendar event:', error);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Handle event website click
  const handleEventWebsiteClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    
    if (displayEvent?.website && user?.uid) {
      // Log the external event website referral activity
      try {
        await activityLogService.logExternalEventWebsiteReferral(user.uid, displayEvent.id);
      } catch (error) {
        console.error('Error logging external event website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      let formattedUrl = displayEvent.website.trim();
      if (!/^https?:\/\//i.test(formattedUrl)) {
        formattedUrl = 'https://' + formattedUrl;
      }
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, use location.href to navigate directly
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open to open in new tab
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Check if user can edit/delete this event
  const canEditEvent = user?.schoolName && (displayEvent?.hostName || displayEvent?.host) === user.schoolName;

  // Initialize edit form when event is loaded
  React.useEffect(() => {
    if (displayEvent) {
      // Initialize calendar event data
      if (user?.profileExtra?.eventsAddedToCalendar?.[displayEvent.id]) {
        setCalendarEventData(user.profileExtra.eventsAddedToCalendar[displayEvent.id]);
      }
      // Fallback logic for startTime
      let startTimeSource = undefined;
      if ('startTime' in displayEvent && displayEvent.startTime) {
        startTimeSource = displayEvent.startTime;
      } else if (displayEvent.date && displayEvent.time) {
        // Convert m/d/yyyy to yyyy-mm-dd
        const [month, day, year] = displayEvent.date.split('/');
        if (month && day && year) {
          const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          // Convert 12-hour time to 24-hour
          const timeMatch = displayEvent.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (timeMatch) {
            let [, hour, minute, ampm] = timeMatch;
            hour = parseInt(hour, 10);
            if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
            if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
            const timeStr = `${hour.toString().padStart(2, '0')}:${minute}`;
            startTimeSource = `${dateStr} ${timeStr}`;
          }
        }
      } else if (displayEvent.isoDate) {
        startTimeSource = displayEvent.isoDate;
      }
      // Instead of parsing as a Date, just split the string and display the hour/minute part
      let startDate = '';
      let startTime = '';
      let startTimeForField = '';
      if (startTimeSource) {
        // Split 'YYYY-MM-DD HH:mm' or 'YYYY-MM-DDTHH:mm'
        const match = startTimeSource.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
        if (match) {
          const [, year, month, day, hour, minute] = match;
          startDate = `${year}-${month}-${day}`;
          let hourNum = parseInt(hour, 10);
          const minNum = parseInt(minute, 10);
          const ampm = hourNum >= 12 ? 'PM' : 'AM';
          let displayHour = hourNum % 12;
          if (displayHour === 0) displayHour = 12;
          startTime = `${displayHour}:${minNum.toString().padStart(2, '0')} ${ampm}`;
          startTimeForField = `${hourNum.toString().padStart(2, '0')}:${minNum.toString().padStart(2, '0')}`;
        } else {
          startDate = 'N/A';
          startTime = 'N/A';
          startTimeForField = '';
        }
      } else {
        startDate = 'N/A';
        startTime = 'N/A';
        startTimeForField = '';
      }
      // Parse end date and time from endTime only (already robust)
      let endDate = '';
      let endTime = '';
      let endTimeForField = '';
      if (displayEvent.endTime) {
        let endTimeSource = displayEvent.endTime;
        if (!endTimeSource && displayEvent.endDate && displayEvent.endTime) {
          // Convert m/d/yyyy to yyyy-mm-dd
          const [month, day, year] = displayEvent.endDate.split('/');
          if (month && day && year) {
            const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            // Convert 12-hour time to 24-hour
            const timeMatch = displayEvent.endTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
            if (timeMatch) {
              let [, hour, minute, ampm] = timeMatch;
              hour = parseInt(hour, 10);
              if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
              if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
              const timeStr = `${hour.toString().padStart(2, '0')}:${minute}`;
              endTimeSource = `${dateStr} ${timeStr}`;
            }
          }
        }
        if (!endTimeSource && displayEvent.endIsoDate) {
          endTimeSource = displayEvent.endIsoDate;
        }
        if (endTimeSource) {
          const match = endTimeSource.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
          if (match) {
            const [, year, month, day, hour, minute] = match;
            endDate = `${year}-${month}-${day}`;
            let hourNum = parseInt(hour, 10);
            const minNum = parseInt(minute, 10);
            const ampm = hourNum >= 12 ? 'PM' : 'AM';
            let displayHour = hourNum % 12;
            if (displayHour === 0) displayHour = 12;
            endTime = `${displayHour}:${minNum.toString().padStart(2, '0')} ${ampm}`;
            endTimeForField = `${hourNum.toString().padStart(2, '0')}:${minNum.toString().padStart(2, '0')}`;
          } else {
            endDate = 'N/A';
            endTime = 'N/A';
            endTimeForField = '';
          }
        } else {
          endDate = 'N/A';
          endTime = 'N/A';
          endTimeForField = '';
        }
      } else {
        endDate = 'N/A';
        endTime = 'N/A';
        endTimeForField = '';
      }
      setParsedStartDate(startDate);
      setParsedStartTime(startTime);
      setParsedEndDate(endDate);
      setParsedEndTime(endTime);
      // Log the parsed values for debugging
      console.log('parsedStartTime:', startTime);
      console.log('parsedEndTime:', endTime);
      const formData = {
        name: displayEvent.title || displayEvent.name || '',
        description: displayEvent.description || '',
        location: displayEvent.address || displayEvent.location || '',
        startDate: startDate !== 'N/A' ? startDate : '',
        startTime: startTimeForField,
        endDate: endDate !== 'N/A' ? endDate : '',
        endTime: endTimeForField,
        website: displayEvent.website || '',
        registerRequired: displayEvent.registerRequired || false,
        eventType: displayEvent.eventType || 'physical',
        hostName: displayEvent.hostName || displayEvent.host || '',
        private: displayEvent.private || false
      };
      setEditForm(formData);
    }
  }, [displayEvent]);

  const handleDelete = async () => {
    if (!displayEvent || !displayEvent.schoolId || !displayEvent.userId) return;
    
    setDeleteLoading(true);
    try {
      const eventRef = ref(database, `schoolEvent/${displayEvent.schoolId}/${displayEvent.userId}/${displayEvent.id}`);
      await remove(eventRef);
      setDeleteModalOpen(false);
      navigate('/events');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!displayEvent || !displayEvent.schoolId || !displayEvent.userId) return;
    
    setEditLoading(true);
    try {
      // Combine date and time fields
      const startDateTime = editForm.startDate && editForm.startTime 
        ? `${editForm.startDate} ${editForm.startTime}` 
        : '';
      const endDateTime = editForm.endDate
        ? `${editForm.endDate} ${editForm.endTime || '00:00'}`
        : '';

      // Log what is being saved
      console.log('Saving:', {
        startDateTime,
        endDateTime,
        editForm
      });

      const eventRef = ref(database, `schoolEvent/${displayEvent.schoolId}/${displayEvent.userId}/${displayEvent.id}/eventContent`);
      const updateData: any = {
        title: editForm.name,
        description: editForm.description,
        address: editForm.location,
        startTime: startDateTime,
        endTime: endDateTime,
        website: editForm.website,
        registerRequired: editForm.registerRequired,
        eventType: editForm.eventType
      };
      
      // Include hostName and private fields if they're being updated
      if (editForm.hostName) {
        updateData.hostName = editForm.hostName;
      }
      if (editForm.private !== undefined) {
        updateData.private = editForm.private;
      }
      
      await update(eventRef, updateData);
      
      // Update local event state immediately
      const updatedEvent = {
        ...displayEvent,
        title: editForm.name,
        description: editForm.description,
        address: editForm.location,
        startTime: startDateTime,
        endTime: endDateTime,
        website: editForm.website,
        registerRequired: editForm.registerRequired,
        eventType: editForm.eventType,
        hostName: editForm.hostName,
        private: editForm.private
      };
      setLocalEvent(updatedEvent);
      
      setEditModalOpen(false);
      refresh(); // Refresh events to show updated data
      setRefreshTrigger(prev => prev + 1); // Force re-render
      
      // Force a manual refresh to ensure Newsfeed updates
      setTimeout(() => {
        refresh();
      }, 100);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  // Helper function to format date for display (mmm dd, yyyy)
  function formatDateMDY(dateStr: string) {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return dateStr;
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month, 10) - 1;
    const monthName = monthNames[monthIndex] || month;
    const dayNum = parseInt(day, 10);
    
    return `${monthName} ${dayNum}, ${year}`;
  }

  // Helper to parse 'YYYY-MM-DD HH:mm' or 'YYYY-MM-DDTHH:mm' as local time
  function parseLocalDateTime(dateTimeStr: string) {
    const match = dateTimeStr && dateTimeStr.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute] = match;
      return new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hour, 10),
        parseInt(minute, 10)
      );
    }
    // Fallback to Date constructor for ISO or other formats
    return new Date(dateTimeStr);
  }

  // Handler for opening map modal
  const handleLocationClick = () => {
    const location = displayEvent.address || displayEvent.location;
    if (location && location.toLowerCase() !== 'virtual') {
      setMapModalOpen(true);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!displayEvent) {
    return <Typography variant="h6">Event not found.</Typography>;
  }

  // Remove formatDateTime and its usage
  // const formatDateTime = () => { ... }
  // const { date: formattedDate, time: formattedTime } = formatDateTime();

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, width: '100%' }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => {
          // Check if user came from profile page
          if (location.state?.fromProfile) {
            navigate('/my-student-profile', { 
              state: { 
                selectedTab: location.state.profileTab 
              } 
            });
          } else if (location.state?.fromSchoolProfile) {
            // Navigate back to school profile with the correct tab
            const schoolId = location.state.event?.schoolId || location.state.event?.hostName;
            navigate(`/schools/${schoolId}`, { 
              state: { 
                selectedTab: location.state.selectedTab 
              } 
            });
          } else {
            navigate(-1);
          }
        }} 
        sx={{ mb: 2 }}
      >
        Back
      </Button>
      
      <Paper sx={{ p: 3 }}>

        {/* Event Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {getSchoolLogo() && (
            <Avatar
              src={getSchoolLogo()}
              alt={displayEvent.hostName || displayEvent.host}
              sx={{ 
                width: 60, 
                height: 60, 
                mr: 2,
                bgcolor: 'grey.200', 
                color: 'grey.600', 
                fontWeight: 600 
              }}
            >
              {(displayEvent.hostName || displayEvent.host) ? (displayEvent.hostName || displayEvent.host)[0] : ''}
            </Avatar>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'left' }}>{displayEvent.title || displayEvent.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {displayEvent.website && (
                <Chip
                  label="View Event Website"
                  clickable
                  color="primary"
                  onClick={(e) => handleEventWebsiteClick(e)}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Host Section */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ minWidth: 90, textAlign: 'left' }}>Host:</Typography>
          <Typography variant="body1" sx={{ textAlign: 'left' }}>{displayEvent.hostName || displayEvent.host}</Typography>
        </Box>

        {/* Description Section */}
        <Typography variant="body1" sx={{ mt: 2, mb: 2, textAlign: 'left', ml: 11.25 }}>{displayEvent.description}</Typography>

        {/* Location Section */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ minWidth: 90, textAlign: 'left' }}>Location:</Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: (displayEvent.address || displayEvent.location)?.toLowerCase() !== 'virtual' ? 'pointer' : 'default',
              '&:hover': {
                color: (displayEvent.address || displayEvent.location)?.toLowerCase() !== 'virtual' ? 'primary.main' : 'inherit',
                textDecoration: (displayEvent.address || displayEvent.location)?.toLowerCase() !== 'virtual' ? 'underline' : 'none'
              }
            }}
            onClick={handleLocationClick}
          >
            <Typography variant="body1" sx={{ textAlign: 'left' }}>
              {displayEvent.address || displayEvent.location}
            </Typography>
            {(displayEvent.address || displayEvent.location)?.toLowerCase() !== 'virtual' && (
              <LocationOnIcon sx={{ ml: 1, fontSize: 20, color: 'primary.main' }} />
            )}
          </Box>
        </Box>

        {/* Registration Required Section */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={!!displayEvent.registerRequired} disabled />}
            label="Registration Required"
            sx={{ textAlign: 'left', m: 0 }}
          />
        </Box>

        {/* Date/Time Section */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, mb: 3, gap: { xs: 2, md: 0 } }}>
          <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ display: 'flex', mb: 1, gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ minWidth: 90, textAlign: 'left' }}>Start Date:</Typography>
                <Typography variant="body1" sx={{ textAlign: 'left' }}>{formatDateMDY(parsedStartDate)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ minWidth: 90, textAlign: 'left' }}>Start Time:</Typography>
                <Typography variant="body1" sx={{ textAlign: 'left' }}>{parsedStartTime}</Typography>
              </Box>
            </Box>
            {displayEvent.endTime && (
              <Box sx={{ display: 'flex', mb: 1, gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ minWidth: 90, textAlign: 'left' }}>End Date:</Typography>
                  <Typography variant="body1" sx={{ textAlign: 'left' }}>{formatDateMDY(parsedEndDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ minWidth: 90, textAlign: 'left' }}>End Time:</Typography>
                  <Typography variant="body1" sx={{ textAlign: 'left' }}>{parsedEndTime}</Typography>
                </Box>
              </Box>
            )}
            
            {/* Event Categories */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {(() => {
                // Handle both single string and array formats for backward compatibility
                const eventCategories = Array.isArray(displayEvent.eventCategory) 
                  ? displayEvent.eventCategory 
                  : displayEvent.eventCategory 
                    ? [displayEvent.eventCategory] 
                    : ['General'];

                return eventCategories.map((category: string, index: number) => (
                  <Chip
                    key={index}
                    label={category}
                    sx={{
                      backgroundColor: '#8dd439', // Fosterway green
                      color: '#000000', // Black text
                      fontWeight: 500
                    }}
                  />
                ));
              })()}
            </Box>
          </Box>

          {/* Calendar Section */}
          <Box sx={{ 
            ml: { xs: 0, md: 4 }, 
            mt: { xs: 2, md: 0 },
            minWidth: { xs: 'auto', md: 200 },
            width: { xs: '100%', md: 'auto' }
          }}>
            {isEventInCalendar() ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-start' }, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    Added to Calendar on {formatCalendarDate(getCalendarEventData()?.timestampAdded || 0)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveFromCalendar}
                  disabled={calendarLoading}
                >
                  {calendarLoading ? <CircularProgress size={16} /> : 'Remove'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-start' }, gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<CalendarTodayIcon />}
                  onClick={(e) => {
                    setCalendarMenuAnchor(e.currentTarget);
                    setCalendarMenuOpen(true);
                  }}
                  disabled={calendarLoading}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  {calendarLoading ? <CircularProgress size={20} /> : 'Add to Calendar'}
                </Button>
                
                <Menu
                  anchorEl={calendarMenuAnchor}
                  open={calendarMenuOpen}
                  onClose={() => setCalendarMenuOpen(false)}
                >
                  <MenuItem onClick={() => handleCalendarAction('ics')}>
                    <ListItemIcon>
                      <DownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Download ICS File</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleCalendarAction('google')}>
                    <ListItemText>Add to Google Calendar</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleCalendarAction('outlook')}>
                    <ListItemText>Add to Outlook</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Box>

        {/* Edit/Delete Buttons */}
        {canEditEvent && (
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditModalOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{displayEvent.title || displayEvent.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Host Name Field - Disabled for Guidance Users */}
            <TextField
              label="Host Name"
              value={editForm.hostName}
              onChange={(e) => handleEditFormChange('hostName', e.target.value)}
              fullWidth
              disabled={user?.isGuidanceUser || false}
              required
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Event Title"
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                sx={{ flex: 2 }}
                required
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={editForm.eventType}
                  onChange={(e) => handleEditFormChange('eventType', e.target.value)}
                  label="Event Type"
                >
                  <MenuItem value="physical">In-person Event</MenuItem>
                  <MenuItem value="virtual">Virtual Event</MenuItem>
                  <MenuItem value="news">News</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => handleEditFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
            />
            
            {/* Physical Location - only shown when Event Type is "In-person Event" */}
            {editForm.eventType === 'physical' && (
              <TextField
                label="Physical Location"
                value={editForm.location}
                onChange={(e) => handleEditFormChange('location', e.target.value)}
                fullWidth
                required
              />
            )}
            
            {/* Online Location - shown for all event types */}
            <TextField
              label="Online Location (optional URL)"
              value={editForm.website}
              onChange={(e) => handleEditFormChange('website', e.target.value)}
              fullWidth
              placeholder="https://example.com"
            />
            
            {/* Event-specific fields - only shown when Event Type is "In-person Event" or "Virtual Event" */}
            {(editForm.eventType === 'physical' || editForm.eventType === 'virtual') && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.registerRequired}
                      onChange={(e) => handleEditFormChange('registerRequired', e.target.checked)}
                    />
                  }
                  label="Registration Required"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.private}
                      onChange={(e) => handleEditFormChange('private', e.target.checked)}
                      disabled={user?.isGuidanceUser || false}
                    />
                  }
                  label="Private Event"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={editForm.startDate}
                    onChange={(e) => handleEditFormChange('startDate', e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                  <TextField
                    label="Start Time"
                    type="time"
                    value={editForm.startTime}
                    onChange={(e) => handleEditFormChange('startTime', e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="End Date (Optional)"
                    type="date"
                    value={editForm.endDate}
                    onChange={(e) => handleEditFormChange('endDate', e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Time (Optional)"
                    type="time"
                    value={editForm.endTime}
                    onChange={(e) => handleEditFormChange('endTime', e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEdit} 
            variant="contained"
            disabled={editLoading || !editForm.name.trim()}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Modal */}
      <MapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        address={displayEvent.address || displayEvent.location}
        eventName={displayEvent.title || displayEvent.name}
      />
    </Box>
  );
};

export default EventDetail; 